import React, { useContext, useEffect, useRef, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { ShopContext } from '../../../Context/ShopContext';
import { ShoppingCartIcon } from '@heroicons/react/24/outline';
import MenuSvg from "../../../assets/MenuSvg";
import { disablePageScroll, enablePageScroll } from "scroll-lock";
import { closeMenu, openMenu } from "../../../animations";
import { navigation } from '../../../constants';

import logo from '../new logo.png';

const Navbar = () => {
  const [openNavigation, setOpenNavigation] = useState(false);
  const [activeSection, setActiveSection] = useState("");
  const [search, setSearch] = useState(''); 
  const [page, setPage] = useState(1); 
  const [limit, setLimit] = useState(10); 
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true); 
  const databaseUrl = process.env.REACT_APP_DATABASE_URL;
  const [openSearch, setOpenSearch] = useState(false);

  const fetchProducts = async () => {
    setLoading(true);
    try {
        const query = new URLSearchParams({
            search,
            page: page.toString(),
            limit: limit.toString(),
        }).toString();

        const response = await fetch(`${databaseUrl}/allproducts?${query}`);
        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();
        if(page === 1 ) setProducts(data.products);
        else setProducts((prev) => [...prev, ...data.products]);

        if (products.length + data.products.length >= data.total) {
            setHasMore(false);
        } 
        else setHasMore(true);
    } catch (error) {
        console.error("Error fetching products:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, [search, page, limit]);

  const toggleNavigation = () => {
    if (openNavigation) {
      closeMenu();
      setOpenNavigation(false);
      enablePageScroll();
    } else {
      openMenu();
      setOpenNavigation(true);
      disablePageScroll();
    }
  };

  const { getTotalCartItems } = useContext(ShopContext);

  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenSearch(false); 
      }
    };

    if (openSearch) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className="flex flex-col items-center justify-center" 
      ref={dropdownRef}
    >
      <div
        className={`fixed top-0 w-full px-[5%] xl:px-[6%] mx-auto z-40 shadow-xl bg-[#551E19] nav-primary`}
      >
        <div className={`flex lg:items-center lg:justify-between px-5 lg:px-7.5 xl:px-10 py-4 lg:py-0 ${openNavigation ? "flex-col justify-start items-start " : "justify-between items-center"}`}>
          <a
            className={`flex items-center xl:mr-8 ${
              openNavigation ? "" : "w-[12rem]"
            }`}
            href="/"
          >
            <img src={logo} alt="poo-poo-shop" className={`w-[120px] sm:w-[150px]  ${openSearch ? 'hidden sm:flex' : ''}`} />
          </a>
          
          <nav
            className={`${
              openNavigation
                ? "flex h-screen justify-center items-start"
                : "hidden"
            } rounded-lg lg:static lg:flex`}
          >
            <div className="lg:bg-white px-10 mt-20 lg:my-[10px] bg-transparent  rounded-full flex items-start lg:items-center lg:shadow-lg relative flex-col justify-center text-center lg:flex-row lg:px-8 xl:px-12 nav-link">
              {navigation.map((item) => (
                <NavLink
                key={item.id}
                to={item.url}
                onClick={() => {
                  closeMenu();
                  setOpenNavigation(false);
                  enablePageScroll();
                }}
                className={({ isActive }) =>
                  `relative font-poppins text-base transition-colors px-4 xl:px-6 py-5 lg:text-sm lg:font-semibold ${
                    item.onlyMobile ? "lg:hidden" : ""
                  } ${
                    isActive
                      ? "text-[#E6B17E] font-semibold"
                      : "text-white lg:text-black hover:text-[#E6B17E] font-semibold"
                  }`
                }
              >
                {item.title}
              </NavLink>
              ))}
            </div>
          </nav>
          <div className='flex gap-4 sm:gap-8'>
            <div className="flex items-center justify-center">
            <div className="relative flex items-center">
  <input
    type="text"
    placeholder="Search..."
    value={search}
    onChange={(e) => setSearch(e.target.value)}
    className={`transition-all duration-300 ease-in-out ${
      openSearch ? 'opacity-100 w-52 xxs:w-60 px-4 py-2' : 'opacity-0 w-0'
    } bg-white text-black border border-gray-300 rounded-full shadow-md focus:outline-none focus:ring-2 focus:ring-[#E6B17E] focus:border-[#E6B17E] text-xs sm:text-sm`}
  />
  <button
    onClick={() => {
      setOpenSearch(!openSearch);
    }}
    className="bg-[#E6B17E] p-2 sm:p-3 my-[6px] sm:my-0 rounded-full text-[#4C1B1C] mx-2 shadow-md hover:scale-105 transition-transform duration-200"
  >
    {openSearch ? (
      <svg
        className="w-5 h-5 sm:w-6 sm:h-6"
        fill="currentColor"
        viewBox="0 0 24 24"
      >
        <path d="M18.3 5.71a1 1 0 0 0-1.41 0L12 10.59 7.11 5.7a1 1 0 0 0-1.41 1.41L10.59 12l-4.89 4.89a1 1 0 1 0 1.41 1.41L12 13.41l4.89 4.89a1 1 0 0 0 1.41-1.41L13.41 12l4.89-4.89a1 1 0 0 0 0-1.41z" />
      </svg>
    ) : (
      <svg
        className="w-5 h-5 sm:w-6 sm:h-6"
        fill="currentColor"
        viewBox="0 0 24 24"
      >
        <path d="M10,2a8,8,0,1,0,5.3,14H16l5,5a1,1,0,0,0,1.41-1.41l-5-5V14.7A8,8,0,0,0,10,2Zm0,2a6,6,0,1,1-6,6A6,6,0,0,1,10,4Z" />
      </svg>
    )}
  </button>
</div>
              <NavLink to="/cart" className={`lg:inline-flex relative h-12 ${openSearch ? 'hidden' : ''}`}>
                <div className="bg-[#E6B17E] p-2 sm:p-3 my-[6px] sm:my-0 rounded-full text-[#4C1B1C] mx-2 shadow-md">
                  <ShoppingCartIcon className="w-5 h-5 sm:w-6 sm:h-6" />
                  <span className="absolute -top-1 -right-[0.1px] bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center shadow-sm">
                    {getTotalCartItems()}
                  </span>
                </div>
              </NavLink>
            </div>
            <button
              className={`lg:hidden ${openSearch ? 'hidden xxs:block' : ''} ${openNavigation ? "z-6 absolute top-9 right-[8%]" : ""}`}
              onClick={toggleNavigation}
            >
              <MenuSvg openNavigation={openNavigation} />
            </button>
          </div>
        </div>
        {search && products.length > 0 && openSearch && (
          <div
            className="absolute top-full left-0 right-0 md:left-[60%] md:right-[10%] bg-white backdrop-blur-3xl backdrop-brightness-75 shadow-lg z-40 rounded-lg max-h-[400px] overflow-y-auto"
          >
            <ul className="list-none p-4 space-y-2">
              {products.map((product) => (
                <li
                  key={product.id}
                  className="py-2 lg:px-2 xl:px-4 hover:bg-gray-200 rounded-lg transition-colors cursor-pointer flex items-center"
                >
                  <NavLink
                    to={`/product/${product.id}`}
                    onClick={() => setSearch('')}
                    className="text-black text-sm font-medium truncate w-full block"
                    style={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {product.name}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default Navbar;
